import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Github, Users, BookOpen, Calendar, Activity } from "lucide-react";
import { useTranslation } from "react-i18next";

const GitHubStats = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const username = "anthonynjeri"; // Your GitHub username

  useEffect(() => {
    const fetchGitHubData = async () => {
      try {
        setLoading(true);

        // Fetch user stats only
        const userResponse = await fetch(
          `https://api.github.com/users/${username}`
        );
        if (!userResponse.ok) throw new Error("Failed to fetch user data");
        const userData = await userResponse.json();

        setStats(userData);
        setError(null);
      } catch (err) {
        console.error("GitHub API Error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGitHubData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <motion.div
          className="w-8 h-8 border-2 border-accent/30 border-t-accent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <p className="text-slate/60 font-mono text-sm">
          {t('github.unableToLoad')}
        </p>
        <p className="text-slate/40 font-mono text-xs mt-2">{error}</p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
    });
  };

  return (
    <motion.div
      className="bg-lightNavy/30 rounded-lg p-6 border border-accent/20"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="flex items-center mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
            <Github className="w-6 h-6 text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-lighterSlate">
              {t('github.title')}
            </h3>
            <p className="text-slate/60 font-mono text-sm">@{username}</p>
          </div>
        </div>
        <motion.a
          href={`https://github.com/${username}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto px-4 py-2 bg-accent/10 text-accent rounded-lg border border-accent/20 hover:bg-accent/20 transition-colors duration-300 font-mono text-sm"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {t('github.viewProfile')}
        </motion.a>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div
          className="text-center p-4 bg-navy/50 rounded-lg border border-accent/10"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          <BookOpen className="w-5 h-5 text-accent mx-auto mb-2" />
          <div className="text-2xl font-bold text-lighterSlate font-mono">
            {stats?.public_repos || 0}
          </div>
          <div className="text-xs text-slate/60 font-mono">{t('github.repositories')}</div>
        </motion.div>

        <motion.div
          className="text-center p-4 bg-navy/50 rounded-lg border border-accent/10"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          <Users className="w-5 h-5 text-accent mx-auto mb-2" />
          <div className="text-2xl font-bold text-lighterSlate font-mono">
            {stats?.followers || 0}
          </div>
          <div className="text-xs text-slate/60 font-mono">{t('github.followers')}</div>
        </motion.div>

        <motion.div
          className="text-center p-4 bg-navy/50 rounded-lg border border-accent/10"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          <Activity className="w-5 h-5 text-accent mx-auto mb-2" />
          <div className="text-2xl font-bold text-lighterSlate font-mono">
            {stats?.following || 0}
          </div>
          <div className="text-xs text-slate/60 font-mono">{t('github.following')}</div>
        </motion.div>

        <motion.div
          className="text-center p-4 bg-navy/50 rounded-lg border border-accent/10"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          <Calendar className="w-5 h-5 text-accent mx-auto mb-2" />
          <div className="text-2xl font-bold text-lighterSlate font-mono">
            {formatDate(stats?.created_at)}
          </div>
          <div className="text-xs text-slate/60 font-mono">{t('github.memberSince')}</div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default GitHubStats;
